import { inject, OnDestroy, Service } from '@angular/core';
import { differenceInMinutes } from 'date-fns';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { IDiagnosticLog } from '../../core/models/IDiagnosticLog';
import { LOCAL_STORAGE_KEY, LocalStorageService } from '../../core/services/local-storage.service';

@Service()
export class DiagnosticsService implements OnDestroy {
    private storageSvc = inject(LocalStorageService);

    public logs: Observable<IDiagnosticLog[]>;

    private readonly key: string;

    private isActive = false;
    private logSubject = new BehaviorSubject<IDiagnosticLog[]>([]);

    constructor() {
        this.key = environment.diagnosticLogKeyBase ? btoa(environment.diagnosticLogKeyBase) : null;
        this.isActive = this.tryActivateService(null);
        this.logs = this.logSubject.asObservable();

        this.populateWithStoredValuesIfNeeded();
    }

    public ngOnDestroy(): void {
        const logs = this.logSubject.getValue();
        if (this.isActive && logs.length > 0) {
            this.storageSvc.set(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS, logs);
        }
    }

    public disable(): void {
        this.clearStorage();
        this.isActive = false;
    }

    public get isActivated(): boolean {
        return this.isActive;
    }

    public tryActivateService(key: string): boolean {
        const areStorageTicksValid = !!this.validLogsEnabledTicks;
        if (!areStorageTicksValid) {
            this.clearStorage();
        }

        this.isActive = (!!this.key && !!key && key === this.key) || !!areStorageTicksValid;

        if (this.isActive && !areStorageTicksValid) {
            this.storageSvc.set(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS_ENABLED_DATE, Date.now());
        }

        return this.isActive;
    }

    public log(item: IDiagnosticLog) {
        if (!this.isActive) {
            return;
        }

        let array = this.logSubject.getValue();
        if (!array) {
            array = [];
        }
        array.splice(0, 0, { ...item, date: new Date().toISOString() as unknown as Date });
        this.storageSvc.set(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS, array);
        this.logSubject.next(array);
    }

    public getCurrentLogs(): { key: string; value: string }[] {
        return this.logSubject.getValue();
    }

    private clearStorage(): void {
        this.storageSvc.remove(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS_ENABLED_DATE);
        this.storageSvc.remove(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS);
    }

    private populateWithStoredValuesIfNeeded(): void {
        if (this.key && this.isActive) {
            const oldValue = this.storageSvc.get<IDiagnosticLog[]>(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS);
            if (oldValue?.length) {
                this.logSubject.next(oldValue);
            }
        }
    }

    private get validLogsEnabledTicks(): number {
        const storageTicks = this.storageSvc.get<number>(LOCAL_STORAGE_KEY.DIAGNOSTIC_LOGS_ENABLED_DATE);
        const areStorageTicksValid = storageTicks && differenceInMinutes(new Date(), new Date(storageTicks)) < 5;

        return areStorageTicksValid ? storageTicks : null;
    }
}
